import { webRequestClient } from '@/services'
import { errorHandler } from '@/utils/helpers'
import { toStartISO, toEndISO, typeToUrlParam } from './helpers'

const actions = {
    /**
     * Retrieve AllAllocations
     * @param {Object} context
     * @param {Object} payload
     * @param {Date} payload.start
     * @param {Date} payload.end
     */
    async getAllAllocations({ commit, rootState }, { start, end }) {
        const params = {
            account_id: rootState.account.accountId,
            start: toStartISO(start),
            end: toEndISO(end)
        }

        try {
            const res = await webRequestClient.get('/audience_admin/activity/frontend/graph/audience/json', { params })
            if (res.status !== 'error' && res.message !== 'no matching \'ids\' found') {
                commit('setAllocationData', { data: res.data.ids[0] })
            }
            commit('setAllocationDataTimeUnit', { timeUnit: res.query.time_unit })
        } catch (error) {
            errorHandler(error, commit)
        }
    },

    /**
     * Retrieve data for top 100 tables
     * @param {Object} context vuex context
     * @param {Object} payload
     * @param {String} payload.type 'audiences' or 'segments'
     */
    async getTop({ commit, rootState, state }, { type }) {
        commit('setAllocationTopLoading', true)

        const params = {
            account_id: rootState.account.accountId,
            top: 100,
            start: toStartISO(state.selectedTimeRange[0]),
            end: toEndISO(state.selectedTimeRange[1])
        }

        try {
            const res = await webRequestClient.get(`/audience_admin/activity/frontend/top/${typeToUrlParam(type)}/json`, { params })
            if (res.status !== 'error' && res.message !== 'no matching \'ids\' found') {
                commit('setAllocationTop', { type, allocations: res.data.ids })
            }
        } catch (error) {
            errorHandler(error, commit)
        } finally {
            commit('setAllocationTopLoading', false)
        }
    },

    /**
     * Retrieve allocation metric for one audience or segment
     * @param {Object} context vuex context
     * @param {payload} payload
     * @param {String} payload.type 'audiences' or 'segments'
     * @param {Number} payload.id
     */
    async getAllocationsBy({ commit, rootState, state }, { id, type }) {
        commit('setLoadingAllocationBy', { id, loading: true, type })

        const params = {
            account_id: rootState.account.accountId,
            id,
            start: toStartISO(state.selectedTimeRange[0]),
            end: toEndISO(state.selectedTimeRange[1])
        }

        try {
            const res = await webRequestClient.get(`/audience_admin/activity/frontend/graph/${typeToUrlParam(type)}/json`, { params })
            commit('setAllocation', { data: res.data.ids[0], id, timeUnit: res.query.time_unit, type })
        } catch (error) {
            errorHandler(error, commit)
        } finally {
            commit('setLoadingAllocationBy', { id, loading: false, type })
        }
    },

    /**
     * Send call to create an alert
     * @param {Object} context vuex context
     * @param {Object} payload
     * @param {String} payload.label label of the alert
     * @param {String} payload.objectType type of object whom we want to create an alert for
     * @param {String} payload.triggerType Type of triggered desired
     * @param {Object} payload.triggerCondition Object describing the desired conditions to trigger alert
     * @param {Object} payload.notification Object that contains the info on specifications about notif (email, cc)
     */
    async createAlert({ commit, rootState }, { label, objectId, objectType, triggerType, triggerCondition, notification }) {
        const requestParams = {
            account_id: rootState.account.accountId,
            label: label,
            object_id: objectId,
            object_type: objectType,
            trigger_type: triggerType,
            trigger_condition: triggerCondition,
            notification: notification
        }
        try {
            const res = await webRequestClient.post('/audience_admin/wam_alerts.json', requestParams)
            commit('setDisplayAlertForm', { value: false })
            commit('notifications/setAddedNotification', { type: 'success', message: 'Alert created' }, { root: true })
            commit('setNewAlert', { alert: res })
        } catch (error) {
            errorHandler(error, commit)
        }
    },

    /**
     * Send call to edit an alert
     * @param {Object} context vuex context
     * @param {Object} payload
     * @param {String} payload.label label of the alert
     * @param {String} payload.triggerType Type of triggered desired
     * @param {Object} payload.triggerCondition Object describing the desired conditions to trigger alert
     * @param {Object} payload.notification Object that contains the info on specifications about notif (email, cc)
     * @param {Object} payload.status Current alert status
     */
    async editAlert({ commit }, { label, triggerType, triggerCondition, notification, id, status }) {
        const requestParams = {
            label,
            trigger_type: triggerType,
            trigger_condition: triggerCondition,
            notification,
            status
        }
        try {
            const res = await webRequestClient.put(`/audience_admin/wam_alerts/${id}.json`, requestParams)
            commit('setDisplayAlertForm', { value: false })
            commit('notifications/setAddedNotification', { type: 'success', message: 'Alert edited' }, { root: true })
            commit('setAlert', { alert: res })
        } catch (error) {
            errorHandler(error, commit)
        }
    },

    async getAlerts({ commit }) {
        commit('setAlertsLoading', true)
        try {
            const params = {
                list_limit: 0,
                conditions: { status: [{ '!=': 'deleted' }] }
            }
            const res = await webRequestClient.get('/audience_admin/wam_alerts/details/json', { params })
            commit('setAlerts', { alerts: res.list })
        } catch (error) {
            errorHandler(error, commit)
        } finally {
            commit('setAlertsLoading', false)
        }
    },

    async getKibanaData({ commit, rootState }, { start, end }) {
        const params = {
            account_id: rootState.account.accountId,
            end: toEndISO(end),
            start: toStartISO(start)
        }

        try {
            const res = await webRequestClient.get('/audience_admin/activity/kibana/frontend/graph/json', { params })
            commit('setKibanaData', { data: res.data.ids[0] })
        } catch (error) {
            errorHandler(error, commit)
        }
    },

    /**
     * Send call to delete an alert
     * @param {Object} context vuex context
     * @param {Number} id id of the alert to delete
     */
    async deleteAlert({ commit }, id) {
        try {
            await webRequestClient.delete(`/audience_admin/wam_alerts/${id}.json`, {})
            commit('notifications/setAddedNotification', { type: 'success', message: 'Alert successfully deleted' }, { root: true })
            commit('setDeletedAlert', { alertId: id })
        } catch (error) {
            commit('notifications/setAddedNotification', { type: 'error', message: 'The alert could not be deleted' }, { root: true })
        }
    }
}

export default actions
